import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Col, Row, Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { RiArrowDropDownLine } from "react-icons/ri";
import { HiOutlineQuestionMarkCircle } from "react-icons/hi";
import { IoLocationOutline } from "react-icons/io5";
import { FaPlayCircle, FaCoffee, FaStop } from "react-icons/fa";

import {
  checkLocation,
  createPresence,
  getCurrentPresence,
  startPause,
  updatePresence,
} from "../../../redux/slices/presenceSlice";
import { endPausePause, fetchPauseReasons } from "../../../redux/slices/pausesSlice";
import { fetchShiftsByUser } from "../../../redux/slices/shiftSlice";

import WorkTimeRegisterModal from "../../Modal/WorkTimeRegisterModal/WorkTimeRegisterModal";
import { openModal, fetchWorkTimeSuggestions } from "../../../redux/slices/workTimeRegisterSlice";
import Intro from "../../reusableComps/Intro/Intro";
import { getTimeLogErrorMessage } from "../../../utils/timeLogging";
import { formatTime as formatConfiguredTime, formatTimeRange } from "../../../utils/timeFormat";
import {
  LOCATION_STATUS,
  LOCATION_STATUS_STYLES,
  WORK_MODES,
  defaultWorkMode,
  formatDistance,
  getCurrentPosition,
} from "../../../utils/workLocation";

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("T");
  if (parts.length === 2) {
    const timePart = parts[1];
    if (!timePart.includes("Z") && !timePart.includes("+") && !timePart.includes("-")) {
      return new Date(`${dateStr}Z`);
    }
  }
  return new Date(dateStr);
};

const formatTime = (ms) => {
  const minutes = Math.floor(ms / 60000);
  const hrs = String(Math.floor(minutes / 60)).padStart(2, "0");
  const mins = String(minutes % 60).padStart(2, "0");
  return `${hrs}:${mins}`;
};

const formatPresenceTime = (timeInput) => {
  if (!timeInput) return "-";
  const date = typeof timeInput === "number" ? new Date(timeInput) : parseDate(timeInput);
  return formatConfiguredTime(date, { locale: "pt-PT" });
};

const MAX_DAILY_WORK_MINUTES = 12 * 60;
const MAX_SHIFT_MINUTES = 24 * 60;

const formatMinutes = (minutes) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

const getModalMessage = (action, t) => {
  switch (action) {
    case "start":
      return t("components.ClockCard.modal_confirm_start");
    case "pause":
      return t("components.ClockCard.modal_confirm_pause");
    case "resume":
      return t("components.ClockCard.modal_confirm_resume");
    case "end":
      return t("components.ClockCard.modal_confirm_end");
    default:
      return t("components.ClockCard.modal_confirm_continue");
  }
};

const ClockCard = ({ className = "", style = {} }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentPresence, loading, error } = useSelector(
    (state) => state.presences,
  );
  const currentUser = useSelector((state) => state.users.currentUser);
  const shifts = useSelector((state) => state.shifts.items);
  
  const pauseReasons = useSelector((state) => state.pauses?.reasons || []);

  const [totalPauseDuration, setTotalPauseDuration] = useState("00:00");
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [presenceType, setPresenceType] = useState("Work");
  const [workLocation, setWorkLocation] = useState("On-site");
  const [position, setPosition] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationCheck, setLocationCheck] = useState(null);
  const [locationNote, setLocationNote] = useState("");
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    // Warms the browser position cache so clock actions do not wait for a fresh fix
    getCurrentPosition();
  }, []);

  useEffect(() => {
    dispatch(getCurrentPresence()).catch((err) => {
      console.error("Error fetching current presence:", err);
    });
    
    if (pauseReasons.length === 0) {
       dispatch(fetchPauseReasons());
    }
  }, [dispatch, pauseReasons.length]);

  useEffect(() => {
    if (currentUser?.id) {
      dispatch(fetchShiftsByUser(currentUser.id)).catch((err) => {
        console.error("Error fetching user shifts:", err);
      });
    }
  }, [dispatch, currentUser]);

  const todayShift = useMemo(() => {
    if (!shifts || shifts.length === 0) return null;
    const today = new Date();
    return shifts.find((shift) => {
      const shiftDate = new Date(shift.start_time);
      return (
        shiftDate.getFullYear() === today.getFullYear() &&
        shiftDate.getMonth() === today.getMonth() &&
        shiftDate.getDate() === today.getDate()
      );
    });
  }, [shifts]);

  const clockState = useMemo(() => {
    if (!currentPresence?.check_in) return "not_started";
    const activePause = currentPresence?.pauses?.some(
      (pause) => pause.start && !pause.end,
    );
    if (activePause) return "paused";
    if (!currentPresence?.check_out) return "working";
    return "not_started";
  }, [currentPresence]);

  const progressSegments = useMemo(() => {
    if (!currentPresence?.check_in) return [];

    const EXPECTED_HOURS = currentPresence.expected_hours || 8;
    const TOTAL_MS = EXPECTED_HOURS * 60 * 60 * 1000;
    const now = currentTime;

    let segments = [];
    const checkInDate = parseDate(currentPresence.check_in);
    let lastTime = checkInDate ? checkInDate.getTime() : now;
    const pauses = currentPresence.pauses || [];

    pauses.forEach((pause) => {
      if (!pause.start) return;
      const pauseStart = parseDate(pause.start).getTime();

      if (pauseStart > lastTime) {
        segments.push({
          type: "work",
          ms: pauseStart - lastTime,
          startTime: lastTime,
          endTime: pauseStart
        });
      }

      const pauseEnd = pause.end ? parseDate(pause.end).getTime() : now;
      segments.push({
        type: "pause",
        ms: Math.max(0, pauseEnd - pauseStart),
        startTime: pauseStart,
        endTime: pauseEnd
      });

      lastTime = pauseEnd;
    });

    if (!currentPresence.check_out) {
      if (clockState === "working") {
        segments.push({
          type: "work",
          ms: Math.max(0, now - lastTime),
          startTime: lastTime,
          endTime: now
        });
      }
    } else {
      const checkOutTime = parseDate(currentPresence.check_out).getTime();
      if (checkOutTime > lastTime) {
        segments.push({
          type: "work",
          ms: checkOutTime - lastTime,
          startTime: lastTime,
          endTime: checkOutTime
        });
      }
    }

    return segments.map((seg) => ({
      ...seg,
      widthPct: (seg.ms / TOTAL_MS) * 100,
    }));
  }, [currentPresence, clockState, currentTime]);

  const workSummary = useMemo(() => {
    const workedMs = progressSegments
      .filter((seg) => seg.type === "work")
      .reduce((acc, seg) => acc + seg.ms, 0);
    const checkIn = parseDate(currentPresence?.check_in);
    // Totals cover the whole day: the 12h limit and the expected hours apply to all its shifts
    const otherShiftsMinutes = currentPresence?.day_other_worked_minutes || 0;
    const workedMinutes = otherShiftsMinutes + Math.floor(workedMs / 60000);
    const shiftMinutes = checkIn ? Math.floor((currentTime - checkIn.getTime()) / 60000) : 0;
    return {
      workedMinutes,
      otherShiftsMinutes,
      expectedMinutes: currentPresence?.day_expected_minutes ?? Math.round((currentPresence?.expected_hours || 8) * 60),
      exceedsLimit: workedMinutes > MAX_DAILY_WORK_MINUTES || shiftMinutes > MAX_SHIFT_MINUTES,
    };
  }, [progressSegments, currentPresence, currentTime]);

  const computeTotalPauseDuration = useCallback(() => {
    const pauses = currentPresence?.pauses || [];
    let totalMs = 0;
    pauses.forEach((pause) => {
      const start = parseDate(pause.start);
      const end = pause.end
        ? parseDate(pause.end)
        : clockState === "paused"
          ? new Date()
          : null;
      if (start && end) {
        totalMs += end.getTime() - start.getTime();
      }
    });
    return formatTime(Math.max(0, totalMs));
  }, [currentPresence, clockState]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
      setTotalPauseDuration(computeTotalPauseDuration());
    }, 10000);
    setTotalPauseDuration(computeTotalPauseDuration());
    return () => clearInterval(interval);
  }, [clockState, currentPresence, computeTotalPauseDuration]);

  const activePauseDisplay = useMemo(() => {
    const pauses = currentPresence?.pauses || [];

    if (clockState === "paused") {
      const activePause = pauses.find((p) => p.start && !p.end);
      if (activePause) {
        return (
          <span>
          ({totalPauseDuration})
          </span>
        );
      }
    }

    const completedPauses = pauses.filter((p) => p.start && p.end);
    if (completedPauses.length > 0) {
      return (
        <span className="d-flex align-items-center justify-content-center gap-1">
          {t("components.ClockCard.total")}: {totalPauseDuration}
          <HiOutlineQuestionMarkCircle 
            style={{ 
              fontSize: '1rem', 
              color: 'var(--gray-400)',
              marginLeft: '4px',
              cursor: 'help'
            }} 
          />
        </span>
      );
    }

    return "-";
  }, [clockState, currentPresence, totalPauseDuration, t]);

  const allPausesTooltip = useMemo(() => {
    const pauses = currentPresence?.pauses || [];
    if (pauses.length === 0) return t("components.ClockCard.no_pauses_recorded");
    
    return pauses.map((p, index) => {
      const start = formatPresenceTime(p.start);
      const end = p.end ? formatPresenceTime(p.end) : t("components.ClockCard.in_progress");
      const reason = p.reason ? ` - ${p.reason}` : "";
      return t("components.ClockCard.pause_summary", { index: index + 1, start, end, reason });
    }).join("\n");
  }, [currentPresence, t]);

  const handleClockAction = (action) => {
    setPendingAction(action);
    setModalMessage(getModalMessage(action, t));
    setShowModal(true);
  };

  useEffect(() => {
    if (!showModal) return undefined;
    let cancelled = false;
    setLocationCheck(null);
    setLocationNote("");
    setIsLocating(true);
    getCurrentPosition().then((result) => {
      if (cancelled) return;
      setPosition(result);
      setIsLocating(false);
    });
    return () => {
      cancelled = true;
    };
  }, [showModal]);

  useEffect(() => {
    if (!showModal || pendingAction !== "start" || isLocating) return undefined;
    let cancelled = false;
    dispatch(checkLocation({ location: position?.location, accuracy: position?.accuracy, mode: workLocation }))
      .unwrap()
      .then((result) => !cancelled && setLocationCheck(result))
      .catch(() => !cancelled && setLocationCheck(null));
    return () => {
      cancelled = true;
    };
  }, [dispatch, showModal, pendingAction, isLocating, position, workLocation]);

  const handleConfirmAction = async (mode = "registered") => {
    setIsConfirming(true);
    try {
      const current = position || (await getCurrentPosition());
      const coords = current?.location || "unknown";
      const accuracy = current?.accuracy ?? null;

      if (pendingAction === "start") {
        await dispatch(
          createPresence({
            check_in: new Date().toISOString(),
            check_in_location: coords,
            check_in_accuracy: accuracy,
            type: presenceType,
            location: workLocation,
            location_note: locationNote.trim() || null,
            expected_hours: todayShift ? todayShift.total_hours : 8.0,
          }),
        ).unwrap();
        toast.success(t("components.ClockCard.toast_start_success"));

      } else if (pendingAction === "pause") {
        if (clockState === "paused") {
          const lastPause = currentPresence.pauses.slice(-1)[0];
          if (!lastPause?.id) throw new Error("ID de pausa inválido");

          await dispatch(
            endPausePause({
              pauseId: lastPause.id,
              end_location: coords,
              end_accuracy: accuracy,
            }),
          ).unwrap();
          toast.success(t("components.ClockCard.toast_resume_success"));
        } else {
          await dispatch(startPause({
            presenceId: currentPresence?.id || "current",
            reason: presenceType,
            start_location: coords,
            start_accuracy: accuracy,
          })).unwrap();
          toast.success(t("components.ClockCard.toast_pause_success"));
        }
      } else if (pendingAction === "resume") {
        const activePauses = currentPresence.pauses.filter(
          (p) => p.start && !p.end,
        );
        const lastActivePause = activePauses[activePauses.length - 1];
        if (!lastActivePause?.id) throw new Error("ID de pausa inválido");

        await dispatch(
          endPausePause({
            pauseId: lastActivePause.id,
            end_location: coords,
            end_accuracy: accuracy,
          }),
        ).unwrap();
        toast.success(t("components.ClockCard.toast_resume_success"));

      } else if (pendingAction === "end") {
        await dispatch(
          updatePresence({
            presenceId: currentPresence?.id || "current",
            check_out_location: coords,
            check_out_accuracy: accuracy,
            mode,
          }),
        ).unwrap();
        
        toast.success(t("components.ClockCard.toast_end_success"));
        dispatch(openModal());
        dispatch(fetchWorkTimeSuggestions());
      }

      await dispatch(getCurrentPresence()).unwrap();
      setCurrentTime(Date.now());
      setShowModal(false);

    } catch (err) {
      toast.error(getTimeLogErrorMessage(err, t, t("components.ClockCard.toast_action_error")));
    } finally {
      setIsConfirming(false);
    }
  };

  if (error && error !== "No active presence found for current user") {
    return (
      <div
        className={`atlar-card d-flex justify-content-center align-items-center ${className}`}
        style={{ minHeight: "150px", height: "100%", ...style }}
      >
        <p className="txt-main fw-bold">{t("components.ClockCard.error_loading_data")}</p>
      </div>
    );
  }

  const isLoading = loading && !currentPresence;

  return (
    <>
      <div
        className={`atlar-card d-flex flex-column ${className}`}
        style={{ padding: "20px 24px", height: "100%", ...style }}
      >
        {!isLoading ? (
          <div className="d-flex flex-column h-100">
            <div className="mb-3">
              <span
                className="fw-bold"
                style={{ fontSize: "0.85rem", color: "var(--gray-400)" }}
              >
                {`${t("components.ClockCard.today_shift")}: ${
                  todayShift
                    ? formatTimeRange(todayShift.start_time, todayShift.end_time)
                    : currentPresence?.shiftName || "08:00 - 17:00"
                }`}
              </span>
            </div>

            <Row className="g-2 mb-3">
              {clockState === "paused" ? (
                <Col>
                  <Button
                    className="w-100 d-flex align-items-center justify-content-center gap-2 rounded-3 border-0 shadow-sm"
                    style={{
                      backgroundColor: "var(--brand-primary)",
                      color: "var(--white)",
                      height: "40px",
                    }}
                    onClick={() => handleClockAction("resume")}
                  >
                    <FaPlayCircle className="fs-5" />
                    <b style={{ fontSize: "14px" }}>{t("components.ClockCard.resume_service")}</b>
                  </Button>
                </Col>
              ) : clockState === "working" ? (
                <>
                  <Col xs={12} sm={6}>
                    <Button
                      className="w-100 d-flex align-items-center justify-content-center gap-2 rounded-3 border-0 shadow-sm"
                      style={{
                        backgroundColor: "var(--gray-200)",
                        color: "var(--text-primary)",
                        height: "40px",
                      }}
                      onClick={() => handleClockAction("pause")}
                    >
                      <FaCoffee className="fs-5" />
                      <b style={{ fontSize: "14px" }}>{t("components.ClockCard.take_pause")}</b>
                    </Button>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Button
                      className="w-100 d-flex align-items-center justify-content-center gap-2 rounded-3 border-0 shadow-sm"
                      style={{
                        backgroundColor: "var(--danger-red)",
                        color: "var(--white)",
                        height: "40px",
                      }}
                      onClick={() => handleClockAction("end")}
                    >
                      <FaStop className="fs-5" />
                      <b style={{ fontSize: "14px" }}>{t("components.ClockCard.register_exit")}</b>
                    </Button>
                  </Col>
                </>
              ) : (
                <Col>
                  <Button
                    className="w-100 d-flex align-items-center justify-content-center gap-2 rounded-3 border-0 shadow-sm"
                    style={{
                      backgroundColor: "var(--brand-primary)",
                      color: "var(--white)",
                      height: "40px",
                    }}
                    onClick={() => handleClockAction("start")}
                  >
                    <FaPlayCircle className="fs-5" />
                    <b style={{ fontSize: "14px" }}>{t("components.ClockCard.start_shift")}</b>
                  </Button>
                </Col>
              )}
            </Row>

            <div className="mb-3 mt-4" style={{ minHeight: "24px" }}>
              {currentPresence?.check_in ? (
                <>
                  <div
                    className="d-flex justify-content-between align-items-center mb-2"
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--gray-400)",
                      fontWeight: "600",
                    }}
                  >
                    <span>{t("components.ClockCard.day_progress")}</span>
                    <span>
                      {formatTime(
                        progressSegments.reduce((acc, seg) => acc + seg.ms, 0),
                      )}{" "}
                      / {currentPresence.expected_hours ? `${String(Math.floor(currentPresence.expected_hours)).padStart(2, '0')}:00` : "08:00"}
                    </span>
                  </div>
                  <div
                    className="w-100 rounded-pill overflow-hidden d-flex"
                    style={{
                      height: "18px",
                      backgroundColor: "var(--gray-200)",
                      boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    {progressSegments.map((seg, idx) => (
                      <div
                        key={idx}
                        style={{
                          height: "100%",
                          width: `${seg.widthPct}%`,
                          backgroundColor:
                            seg.type === "work"
                              ? "var(--brand-primary)"
                              : "var(--progress-pause)",
                          transition: "width 1s linear, background-color 0.5s ease",
                        }}
                        title={`${seg.type === "work" ? t("components.ClockCard.reasons.work") : t("components.ClockCard.pauses")}: ${formatPresenceTime(seg.startTime)} ${t("components.ClockCard.to_range")} ${formatPresenceTime(seg.endTime)}`}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ height: "18px" }}></div>
              )}
            </div>

            <div
              className="pt-2 mt-auto text-center txt-main"
              style={{ borderTop: "1px solid var(--gray-200)" }}
            >
              <Row className="align-items-center">
                <InfoColumn
                  label={t("components.ClockCard.check_in")}
                  value={formatPresenceTime(currentPresence?.check_in)}
                />
                <Col
                  style={{
                    borderLeft: "1px solid var(--gray-200)",
                    borderRight: "1px solid var(--gray-200)",
                    cursor: "help"
                  }}
                  title={allPausesTooltip}
                >
                  <InfoColumn
                    label={t("components.ClockCard.pauses")}
                    active={clockState === "paused"}
                    value={activePauseDisplay}
                  />
                </Col>
                <InfoColumn
                  label={t("components.ClockCard.check_out")}
                  value={formatPresenceTime(currentPresence?.check_out)}
                />
              </Row>
            </div>
          </div>
        ) : (
          <div className="m-auto p-4 text-center">
            <Spinner
              animation="border"
              style={{ width: 40, height: 40, color: "var(--brand-primary)" }}
            />
          </div>
        )}
      </div>

      <ConfirmationModal
        show={showModal}
        action={pendingAction}
        message={modalMessage}
        workSummary={workSummary}
        presenceType={presenceType}
        setPresenceType={setPresenceType}
        workLocation={workLocation}
        setWorkLocation={setWorkLocation}
        defaultMode={defaultWorkMode(todayShift, currentUser)}
        onHide={() => setShowModal(false)}
        onConfirm={handleConfirmAction}
        userCoords={position?.location}
        isLocating={isLocating}
        locationCheck={locationCheck}
        locationNote={locationNote}
        setLocationNote={setLocationNote}
        isConfirming={isConfirming}
        pauseReasons={pauseReasons}
      />
      <WorkTimeRegisterModal />
    </>
  );
};

const InfoColumn = ({ label, value, active }) => (
  <Col className="px-1">
    <div
      style={{
        fontSize: "0.75rem",
        color: "var(--gray-400)",
        fontWeight: "600",
        marginBottom: "2px",
      }}
    >
      {label}
    </div>
    <div
      className="d-flex align-items-center justify-content-center fw-bold"
      style={{ fontSize: "1rem" }}
    >
      {active && (
        <span
          style={{
            display: "inline-block",
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: "var(--brand-primary)",
            marginRight: "6px",
            boxShadow: "0 0 6px var(--brand-primary)",
          }}
        />
      )}
      {value}
    </div>
  </Col>
);

const LocationCheckNotice = ({ isLocating, locationCheck, locationNote, setLocationNote }) => {
  const { t } = useTranslation();

  if (isLocating || !locationCheck) {
    return (
      <div className="mt-3 d-flex align-items-center gap-2 txt-subtle" style={{ fontSize: "0.85rem" }}>
        <Spinner animation="border" size="sm" />
        {t("components.ClockCard.location_check.locating")}
      </div>
    );
  }

  const { status, office_name: office, distance_meters: distance, first_telework_location: isFirst } = locationCheck;
  const messageKey = status === LOCATION_STATUS.HOME && isFirst ? "home_first" : status;
  const style = LOCATION_STATUS_STYLES[status] || LOCATION_STATUS_STYLES.unknown;

  return (
    <div className="mt-3">
      <div
        role="status"
        className="p-2 rounded-3 fw-semibold"
        style={{ backgroundColor: style.bg, color: style.color, fontSize: "0.85rem" }}
      >
        {t(`components.ClockCard.location_check.${messageKey}`, {
          office: office || "",
          distance: distance != null ? formatDistance(distance) : "",
        })}
      </div>
      {status === LOCATION_STATUS.OTHER && (
        <textarea
          className="form-control mt-2"
          rows={2}
          maxLength={500}
          value={locationNote}
          onChange={(e) => setLocationNote(e.target.value)}
          placeholder={t("components.ClockCard.location_check.note_placeholder")}
          aria-label={t("components.ClockCard.location_check.note_placeholder")}
        />
      )}
    </div>
  );
};

export const ConfirmationModal = ({
  show,
  action,
  message,
  workSummary,
  presenceType,
  setPresenceType,
  workLocation,
  setWorkLocation,
  defaultMode = "On-site",
  onHide,
  onConfirm,
  userCoords,
  isLocating = false,
  locationCheck = null,
  locationNote = "",
  setLocationNote = () => {},
  isConfirming,
  pauseReasons,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (action === "start") {
      setPresenceType("Work");
      setWorkLocation(defaultMode);
    } else if (action === "pause") {
      setPresenceType(pauseReasons.length > 0 ? pauseReasons[0] : "lunch");
    }
  }, [action, setPresenceType, setWorkLocation, defaultMode, pauseReasons]);

  const isEnd = action === "end";
  const isOverExpected = workSummary.workedMinutes > workSummary.expectedMinutes;
  const needsLocationNote =
    action === "start" && locationCheck?.status === LOCATION_STATUS.OTHER && !locationNote.trim();

  return (
    <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="txt-main fw-bold w-100 text-center">
          {t("components.ClockCard.register_point")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        <div
          className="text-center txt-main mb-4"
          style={{ fontSize: "1.1rem" }}
        >
          {message}
        </div>

        {isEnd && (
          <div
            className="mb-3 p-3 rounded-3 txt-main"
            style={{ border: "1px solid var(--gray-200)", backgroundColor: "var(--bg-surface)" }}
          >
            <div className="d-flex justify-content-between">
              <span>{t("components.ClockCard.registered_time")}</span>
              <b>{formatMinutes(workSummary.workedMinutes)}</b>
            </div>
            <div className="d-flex justify-content-between">
              <span>{t("components.ClockCard.expected_time")}</span>
              <b>{formatMinutes(workSummary.expectedMinutes)}</b>
            </div>
            {workSummary.otherShiftsMinutes > 0 && (
              <div className="mt-2 txt-subtle" style={{ fontSize: "0.85rem" }}>
                {t("components.ClockCard.includes_other_shifts", { time: formatMinutes(workSummary.otherShiftsMinutes) })}
              </div>
            )}
            {workSummary.otherShiftsMinutes > 0 && workSummary.otherShiftsMinutes >= workSummary.expectedMinutes && (
              <div className="mt-2 fw-semibold" style={{ fontSize: "0.85rem" }}>
                {t("components.ClockCard.expected_discards_shift")}
              </div>
            )}
            {workSummary.exceedsLimit && (
              <div className="mt-2 fw-semibold" style={{ color: "var(--danger-red)", fontSize: "0.85rem" }}>
                {t("components.ClockCard.daily_limit_warning")}
              </div>
            )}
          </div>
        )}

        {action && (
          <div
            className="mb-3 rounded-3 overflow-hidden"
            style={{ border: "1px solid var(--gray-200)" }}
          >
            {navigator.geolocation ? (
              <Intro height={300} usercoords={userCoords} />
            ) : (
              <div className="p-4 text-center txt-main">
                {t("components.ClockCard.geolocation_unsupported")}
              </div>
            )}
          </div>
        )}

        {(action === "start" || action === "pause") && (
          <div
            style={{
              backgroundColor: "var(--bg-surface)",
              border: "1px solid var(--gray-200)",
              padding: "12px 0px",
              color: "var(--text-primary)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "relative",
              marginBottom: action === "start" ? "12px" : "0px"
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "0px 16px",
              }}
            >
              <IoLocationOutline
                style={{ fontSize: "1.8rem", color: "var(--brand-primary)" }}
              />
              <div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--gray-400)",
                  fontWeight: "600",
                }}
              >
                {t("components.ClockCard.reason")}
              </div>
              <div style={{ fontWeight: "600", fontSize: "1rem" }}>
                {action === "start"
                  ? t(`components.ClockCard.work_types.${presenceType}`, presenceType)
                  : t(`components.ClockCard.reasons.${presenceType}`, presenceType)}
              </div>
            </div>
            </div>

            <div
              style={{
                fontSize: "1.2rem",
                padding: "0px 16px",
                color: "var(--gray-400)",
              }}
            >
              <RiArrowDropDownLine style={{ fontSize: "2.5rem" }} />
            </div>

            <select
              value={presenceType}
              onChange={(e) => setPresenceType(e.target.value)}
              style={{
                position: "absolute",
                opacity: 0,
                width: "100%",
                height: "100%",
                cursor: "pointer",
              }}
            >
              {action === "start" ? (
                <>
                  <option value="Work">{t("components.ClockCard.work_types.Work")}</option>
                  <option value="Meeting">{t("components.ClockCard.work_types.Meeting")}</option>
                </>
              ) : (
                <>
                  {pauseReasons.map((reason, idx) => (
                    <option key={idx} value={reason}>
                      {t(`components.ClockCard.reasons.${reason}`, reason)}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        )}

        {action === "start" && (
          <div
            style={{
              backgroundColor: "var(--bg-surface)",
              border: "1px solid var(--gray-200)",
              padding: "12px 0px",
              color: "var(--text-primary)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "0px 16px",
              }}
            >
              <IoLocationOutline
                style={{ fontSize: "1.8rem", color: "var(--brand-primary)" }}
              />
              <div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--gray-400)",
                    fontWeight: "600",
                  }}
                >
                  {t("components.ClockCard.location")}
                </div>
                <div style={{ fontWeight: "600", fontSize: "1rem" }}>
                  {t(`components.ClockCard.work_locations.${workLocation}`, workLocation)}
                </div>
              </div>
            </div>

            <div
              style={{
                fontSize: "1.2rem",
                padding: "0px 16px",
                color: "var(--gray-400)",
              }}
            >
              <RiArrowDropDownLine style={{ fontSize: "2.5rem" }} />
            </div>

            <select
              value={workLocation}
              onChange={(e) => setWorkLocation(e.target.value)}
              style={{
                position: "absolute",
                opacity: 0,
                width: "100%",
                height: "100%",
                cursor: "pointer",
              }}
            >
              {WORK_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {t(`components.ClockCard.work_locations.${mode}`)}
                </option>
              ))}
            </select>
          </div>
        )}

        {action === "start" && (
          <LocationCheckNotice
            isLocating={isLocating}
            locationCheck={locationCheck}
            locationNote={locationNote}
            setLocationNote={setLocationNote}
          />
        )}

        <div
          className="text-center mt-4"
          style={{ fontSize: "0.85rem", color: "var(--gray-400)" }}
        >
          {t("components.ClockCard.location_sharing_notice")}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        {isEnd ? (
          <div className="w-100 d-flex flex-column flex-sm-row gap-2">
            <Button
              className="flex-fill rounded-3 border-0 shadow-sm"
              style={{ backgroundColor: "var(--brand-primary)", color: "var(--white)", minHeight: "48px" }}
              onClick={() => onConfirm("registered")}
              disabled={isConfirming || workSummary.exceedsLimit}
            >
              {isConfirming ? <Spinner animation="border" size="sm" /> : (
                <b style={{ fontSize: "15px" }}>{t("components.ClockCard.save_registered_time")}</b>
              )}
            </Button>
            <Button
              className="flex-fill rounded-3 shadow-sm"
              variant="outline-primary"
              style={{ minHeight: "48px" }}
              onClick={() => onConfirm("expected")}
              disabled={isConfirming || !isOverExpected}
            >
              <b style={{ fontSize: "15px" }}>{t("components.ClockCard.save_expected_time")}</b>
            </Button>
          </div>
        ) : (
        <Button
          className="w-100 d-flex align-items-center justify-content-center gap-2 rounded-3 border-0 shadow-sm"
          style={{
            backgroundColor: "var(--brand-primary)",
            color: "var(--white)",
            height: "48px",
          }}
          onClick={() => onConfirm()}
          disabled={isConfirming || needsLocationNote}
        >
          {isConfirming ? (
             <Spinner animation="border" size="sm" />
          ) : (
             <FaPlayCircle className="fs-4" />
          )}
          <b style={{ fontSize: "15px" }}>{isConfirming ? t("components.ClockCard.processing") : t("components.ClockCard.confirm")}</b>
        </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ClockCard;
